import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { Drawer, drawerType } from '../../../../packages/ui/organisms/drawer';
import { IconButton } from '../../../../packages/ui/molecules/icon-button';
import { fontWeight, Text } from '../../../../packages/ui/atoms/typography';
import { Button, btnType } from '../../../../packages/ui/atoms/button';
import { Stepper } from '../../../../packages/ui/molecules/stepper';
import { Icons } from '../../../../packages/icons';
import { colors } from '../../../../packages/colors';
// components
import StepOne from './step-one/StepOne';
import StepTwoMedications from './step-two/StepTwoMedications';
import StepFourMedications from './step-four/StepFourMedications';
import { stepTitlesMedications, MEDICATION_MODE } from './AddNewReport.constants';
// styles
import { useStyles } from './AddNewReport.styles';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import { MEDICATION_FREQUENCIES } from '../../../../constants/CommonConstants';
import SocketClient from '../../../../utils/SocketClient';
import { getAssignedPrioritiesV2 } from '../../../../services/priority/priority.service';
import { showSnackbar } from '../../../../redux/modules/snackbar';
import { stateCodes } from '../../../admin/member-list/MemberList.constants';

const AddNewReportMedications = ({
  isOpen,
  isNoDrawer,
  onClose,
  options,
  onSubmit,
  lookupData,
  optionsLoading = false,
  onStartAutomation,
  shouldStartAutomation = false,
  setShouldStartAutomation,
  isAutomationLoading = false,
  automationSessionId = null,
  // Mode and medication props - support both old isEditMode and new mode prop
  mode: modeProp,
  isEditMode = false, // backward compatibility
  existingMedication = null,
  isSaving = false,
  onAutomationError = () => {},
  onAutomationSuccess = () => {},
}) => {
  // Resolve mode from either new mode prop or legacy isEditMode
  const mode = modeProp || (isEditMode ? MEDICATION_MODE.UPDATE : MEDICATION_MODE.ADD);

  const classes = useStyles();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = React.useState(
    mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE ? 2 : 0,
  );
  const [patientDiagnoses, setPatientDiagnoses] = React.useState([]);
  const [continueButtonText, setContinueButtonText] = React.useState(null);
  const [securityCodeAttempts, setSecurityCodeAttempts] = useState(0);
  const { demographicDetails } = useSelector(getProfile);
  const [selectedMedication, setSelectedMedication] = React.useState([]);
  const [selectedItemsForm, setSelectedItemsForm] = React.useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = React.useState(null);
  // MDToolbox automation state
  const [isAutomationRunning, setIsAutomationRunning] = useState(false);
  const [automationMessages, setAutomationMessages] = useState([]);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [socket, setSocket] = useState(null);
  const [userInputRequest, setUserInputRequest] = useState(null);
  const [hasStartedAutomation, setHasStartedAutomation] = useState(false);
  const [isUIFrozen, setIsUIFrozen] = useState(false);
  const [automationCancelled, setAutomationCancelled] = useState(false);

  const createUniqueAutomationMessage = useCallback(
    rawMessage => {
      const { type, message } = rawMessage;
      if (type === 'warning' || type === 'error' || message?.toLowerCase().includes('user input')) {
        // Always add warning messages (user input requests)
        if (message?.toLowerCase().includes('disconnecting from eprescribe')) {
          return;
        }
        setAutomationMessages(prev => [...prev, { type, message, timestamp: new Date() }]);
        return;
      }
      // De-duplicate messages based on message content to improve UX
      setAutomationMessages(prev => {
        return prev.some(msg => msg.message === message)
          ? prev
          : [...prev, { type, message, timestamp: new Date() }];
      });
    },
    [automationMessages],
  );

  // Transform existing medication data to match the expected format
  const transformExistingMedication = (medication, options) => {
    if (!medication) return null;

    // Handle both raw API format (taxonomyName) and legacy format (name)
    const medicationName = medication.name || medication.taxonomyName || '';

    // Find the matching option group for this medication
    let groupId = null;
    let itemOpt = null;
    options.forEach(opt => {
      const result = opt.items.find(it => it.value === medication.domainElementId);
      if (result) {
        groupId = opt.groupId;
        itemOpt = result;
      }
    });

    // Transform frequency to expected format
    const frequencyObj = medication?.tagMetaData?.rxDrugInfo?.frequency
      ? MEDICATION_FREQUENCIES.map(frequency => ({
          label: frequency.display,
          title: frequency.display,
          value: frequency.abbr,
        })).find(v => v.value === medication?.tagMetaData?.rxDrugInfo?.frequency)
      : '';

    // Create externalRx object for the autocomplete field
    const externalRx = medication?.tagMetaData?.rxDrugInfo?.externalMedicationId
      ? {
          label: medication?.tagMetaData?.rxDrugInfo?.externalMedicationName,
          value: medication?.tagMetaData?.rxDrugInfo?.externalMedicationId,
        }
      : null;

    return {
      groupId,
      item: itemOpt || { value: medication.domainElementId, label: medicationName },
      status: medication.priority?.name || medication.currentImportanceLevel || 'TO_BE_PRESCRIBED',
      notes: medication.notes || medication.note || '',
      isInterference: false,
      // Form fields - these should be directly on the object, not in tutorial
      sig: medication?.tagMetaData?.rxDrugInfo?.sig,
      externalRx: externalRx,
      externalMedicationId: medication?.tagMetaData?.rxDrugInfo?.externalMedicationId,
      externalMedicationName: medication?.tagMetaData?.rxDrugInfo?.externalMedicationName,
      refills: medication?.tagMetaData?.rxDrugInfo?.refills || 0,
      dispenseAmount: medication?.tagMetaData?.rxDrugInfo?.dispenseAmount,
      associatedDiagnoses: medication?.tagMetaData?.rxDrugInfo?.associatedDiagnoses || [],
      allowSubstitutions: medication?.tagMetaData?.rxDrugInfo?.allowSubstitutions || true,
      pharmacyNotes: medication?.tagMetaData?.rxDrugInfo?.pharmacyNotes,
      additionalNotes: medication?.tagMetaData?.rxDrugInfo?.additionalNotes,
      quantity: medication?.tagMetaData?.rxDrugInfo?.quantity,
      dose: medication?.tagMetaData?.rxDrugInfo?.dose,
      frequency: frequencyObj,
      duration: medication?.tagMetaData?.rxDrugInfo?.duration,
      confidantProviderId:
        medication?.tagMetaData?.rxDrugInfo?.confidantProviderId ||
        medication?.tagMetaData?.rxDrugInfo?.confidantProvider,
      confidantProvider:
        medication?.tagMetaData?.rxDrugInfo?.confidantProviderId ||
        medication?.tagMetaData?.rxDrugInfo?.confidantProvider,
      confidantProviderName: medication?.tagMetaData?.rxDrugInfo?.confidantProviderName,
      provider:
        medication?.tagMetaData?.rxDrugInfo?.prescribingProvider === 'Internal'
          ? 'Confidant Provider'
          : 'External Provider',
      providerName: medication?.tagMetaData?.rxDrugInfo?.providerName,
      symptoms: medication?.tagMetaData?.rxDrugInfo?.symptomsBeingTreated || [],
      // Pharmacy data
      pharmacyId: medication?.tagMetaData?.rxDrugInfo?.pharmacyId,
      pharmacyName: medication?.tagMetaData?.rxDrugInfo?.pharmacyName,
      pharmacyCode: medication?.tagMetaData?.rxDrugInfo?.pharmacyCode,
      pharmacyZipCode: medication?.tagMetaData?.rxDrugInfo?.pharmacyZipCode,
      // Also create tutorial object for backward compatibility
      tutorial: {
        sig: medication?.tagMetaData?.rxDrugInfo?.sig,
        externalMedicationId: medication?.tagMetaData?.rxDrugInfo?.externalMedicationId,
        externalMedicationName: medication?.tagMetaData?.rxDrugInfo?.externalMedicationName,
        refills: medication?.tagMetaData?.rxDrugInfo?.refills || 0,
        dispenseAmount: medication?.tagMetaData?.rxDrugInfo?.dispenseAmount,
        associatedDiagnoses: medication?.tagMetaData?.rxDrugInfo?.associatedDiagnoses || [],
        allowSubstitutions: medication?.tagMetaData?.rxDrugInfo?.allowSubstitutions || true,
        pharmacyNotes: medication?.tagMetaData?.rxDrugInfo?.pharmacyNotes,
        additionalNotes: medication?.tagMetaData?.rxDrugInfo?.additionalNotes,
        quantity: medication?.tagMetaData?.rxDrugInfo?.quantity,
        dose: medication?.tagMetaData?.rxDrugInfo?.dose,
        frequency: frequencyObj,
        duration: medication?.tagMetaData?.rxDrugInfo?.duration,
        confidantProviderId: medication?.tagMetaData?.rxDrugInfo?.confidantProviderId,
        confidantProviderName: medication?.tagMetaData?.rxDrugInfo?.confidantProviderName,
        prescribingProvider:
          medication?.tagMetaData?.rxDrugInfo?.prescribingProvider === 'Internal'
            ? 'Confidant Provider'
            : 'External Provider',
        providerName: medication?.tagMetaData?.rxDrugInfo?.providerName,
        symptomsBeingTreated: medication?.tagMetaData?.rxDrugInfo?.symptomsBeingTreated || [],
      },
    };
  };

  const changeContinueButtonText = isError => {
    if (isError) {
      if (!continueButtonText) {
        setContinueButtonText('Try again');
      } else if (continueButtonText === 'Try again') {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Something Went Wrong. The team will submit this prescription on your behalf',
          }),
        );
        setShouldStartAutomation(false);
        setHasStartedAutomation(false);
        setIsUIFrozen(false);
        setIsAutomationRunning(false);
        setContinueButtonText('Done');
      }
    } else {
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'ePrescribe submission completed successfully',
        }),
      );
      setIsUIFrozen(false);
      setIsAutomationRunning(false);
      setHasStartedAutomation(false);
      setContinueButtonText('Done');
    }
  };

  const startMdToolboxAutomation = sessionId => {
    if (!sessionId) {
      console.error('No session id provided for ePrescribe automation');
      return;
    }

    if (isAutomationRunning || hasStartedAutomation) {
      console.log('Automation already running, started, or currently saving, skipping duplicate call');
      return;
    }

    setIsAutomationRunning(true);
    setIsUIFrozen(true);
    // setCurrentStep(3); // Go to review step for automation

    // Call the onStartAutomation callback if provided
    if (onStartAutomation) {
      onStartAutomation();
    }

    // Initialize socket connection AFTER getting sessionId
    try {
      const socketClient = SocketClient.getInstance();
      if (socketClient) {
        const connectedSocket = socketClient.getConnectedSocket();
        setSocket(connectedSocket);

        // Listen for automation events
        connectedSocket.on('mdtoolbox_automation_start', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          createUniqueAutomationMessage({
            type: 'success',
            message: 'Connected with ePrescribe',
            timestamp: new Date(),
          });
        });

        connectedSocket.on('automation_status_updates', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          const eventData = data.data || data;
          const { status, message, step } = eventData;
          // Update automation status
          setAutomationStatus({ status, message, step });
          // Add to messages
          createUniqueAutomationMessage({
            type: status,
            message: message,
            timestamp: new Date(),
          });

          // If error occurs, unfreeze the UI but keep drawer open
          if (status === 'error') {
            console.log('Automation error detected - unfreezing UI');
            // Notify parent to reset loading states
            onAutomationError();
            setIsUIFrozen(false);
            setIsAutomationRunning(false);
            setHasStartedAutomation(false);
            changeContinueButtonText(true);
          }
        });

        connectedSocket.on('user_input_request', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          // Extract data from nested structure if present
          const eventData = data.data || data;
          // Handle the new alert structure with multiple alerts
          if (eventData.alerts && Array.isArray(eventData.alerts)) {
            setUserInputRequest({
              ...eventData,
              sessionId: sessionId,
              alertCount: eventData.alerts.length,
              drugName: eventData.drugName,
              actions: eventData.actions || ['cancel', 'override'],
            });
          } else {
            // Fallback for old structure
            setUserInputRequest({ ...eventData, sessionId: sessionId });
          }
          createUniqueAutomationMessage({
            type: 'alert',
            message:
              eventData.type === 'security_code_request'
                ? 'Security Code Required'
                : `Your input is required on ${eventData.drugName}`,
            timestamp: new Date(),
          });
        });

        connectedSocket.on('user_input_response', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          setUserInputRequest(null);
          createUniqueAutomationMessage({
            type: 'info',
            message: 'User input processed',
            timestamp: new Date(),
          });
        });

        connectedSocket.on('mdtoolbox_automation_end', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          setAutomationStatus({
            status: 'success',
            message: 'Submission sent to ePrescribe',
            step: 'Complete',
          });
          createUniqueAutomationMessage({
            type: 'success',
            message: 'Submission sent to ePrescribe',
            timestamp: new Date(),
          });
          onAutomationSuccess();
          setIsAutomationRunning(false);
          setIsUIFrozen(false);
          changeContinueButtonText(false);
        });
      } else {
        console.error('Failed to get socket client instance');
        setAutomationStatus({
          status: 'error',
          message: 'Failed to connect to ePrescribe',
          step: 'Connection',
        });
        // Notify parent to reset loading states
        onAutomationError();
        changeContinueButtonText(true);
        setIsUIFrozen(false);
        setIsAutomationRunning(false);
        // Notify parent to reset loading states
        onAutomationError();
      }
    } catch (error) {
      console.error('Failed to initialize socket:', error);
      setAutomationStatus({
        status: 'error',
        message: 'Failed to connect to ePrescribe',
        step: 'Connection',
      });
      createUniqueAutomationMessage({
        type: 'error',
        message: 'Failed to connect to ePrescribe',
        timestamp: new Date(),
      });
      // Notify parent to reset loading states
      onAutomationError();
      setIsUIFrozen(false);
      setIsAutomationRunning(false);
      changeContinueButtonText(true);
    }
  };

  const handleUserInputResponse = (action, data = {}) => {
    if (socket && userInputRequest) {
      // Use sessionId from automation response if available, otherwise use userInputRequest.userId
      const userId = userInputRequest.sessionId || userInputRequest.userId;
      socket.emit('user_input_response', {
        type: userInputRequest.type,
        action,
        data,
        userId,
      });
      setUserInputRequest(null);
      if (action === 'cancel') {
        // Reset all automation states
        setAutomationCancelled(true);
        setIsAutomationRunning(false);
        setHasStartedAutomation(false);
        setIsUIFrozen(false);
        setAutomationMessages([]);
        setAutomationStatus(null);
        // Navigate to step 1
        setCurrentStep(1);
        return;
      }
    }
  };

  const getMemberAddress = () => {
    // Line 1: address1, address2
    const line1Parts = [demographicDetails.address1, demographicDetails.address2].filter(Boolean);
    // Line 2: city, state, zip
    const line2Parts = [
      demographicDetails.city,
      stateCodes[demographicDetails.state],
      demographicDetails.zip,
    ].filter(Boolean);

    return {
      line1: line1Parts.join(', ') || '',
      line2: line2Parts.join(', ') || '',
      full: line1Parts.concat(line2Parts).join(', ') || 'Missing',
    };
  };

  const initAutomationStates = () => {
    setAutomationMessages([]);
    setIsAutomationRunning(false);
    setAutomationStatus(null);
    setSecurityCodeAttempts(0);
  };

  const handleQueued = () => {
    createUniqueAutomationMessage({
      type: 'info',
      message: `Give us 2-3 minutes. We'll start processing your request shortly`,
      timestamp: new Date(),
    });
  };

  const onClickContinue = () => {
    if (currentStep === 0) {
      // Always update selectedItemsForm when moving from step 0 to ensure it reflects current selection
      const newMedications = selectedMedication.map(item => {
        // Check if we already have data for this medication
        const existingMedication = selectedItemsForm.find(med => med.item?.value === item);

        if (existingMedication) {
          // Preserve existing medication data
          return existingMedication;
        }

        // Create new medication object for new selections
        let groupId = null;
        let itemOpt = null;
        options.forEach(opt => {
          const result = opt.items.find(it => it.value === item);
          if (result) {
            groupId = opt.groupId;
            itemOpt = result;
          }
        });
        return {
          groupId,
          item: itemOpt,
          status: 'TO_BE_PRESCRIBED',
          notes: '',
          isInterference: false,
        };
      });
      setSelectedItemsForm(newMedications);
    }
    if (currentStep === 2) {
      setAutomationCancelled(false);
      if (continueButtonText) {
        if (continueButtonText === 'Done') {
          onClose();
        } else {
          initAutomationStates();
          onSubmit({ medications: selectedItemsForm, pharmacy: selectedPharmacy }, handleQueued);
        }
      } else {
        initAutomationStates();
        onSubmit({ medications: selectedItemsForm, pharmacy: selectedPharmacy }, handleQueued);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const getPreferredPharmacyZip = () => {
    // first medication -> pharmacyZipcode -> fallback to preferred pharmacy zipcode -> fallback to member zipcode
    return (
      selectedItemsForm?.[0]?.preferredPharmacyZipCode ||
      demographicDetails?.preferredPharmacyZipCode ||
      demographicDetails?.zip
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE ? 2 : 0);
      setSelectedMedication([]);
      setSelectedItemsForm([]);
      setSelectedPharmacy(null);
      setHasStartedAutomation(false);
      // Clear messages only when drawer closes
      setAutomationMessages([]);
      setAutomationStatus(null);
      setContinueButtonText(null);
    } else {
      // Set current step based on mode when drawer opens
      setCurrentStep(mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE ? 2 : 0);
      // Reset automation state when drawer opens
      setIsAutomationRunning(false);
      setUserInputRequest(null);
      setHasStartedAutomation(false);
    }
  }, [isOpen, mode]);

  // Start automation when shouldStartAutomation is true
  useEffect(() => {
    if (
      shouldStartAutomation &&
      automationSessionId &&
      !isAutomationRunning &&
      !hasStartedAutomation &&
      !automationCancelled
    ) {
      startMdToolboxAutomation(automationSessionId);
      setHasStartedAutomation(true);
    }
  }, [
    shouldStartAutomation,
    automationSessionId,
    isAutomationRunning,
    hasStartedAutomation,
    automationCancelled,
  ]);

  // Transform pharmacy data from medication record
  const transformPharmacyFromMedication = medication => {
    if (!medication?.tagMetaData?.rxDrugInfo?.pharmacyId) return null;
    return {
      id: medication.tagMetaData.rxDrugInfo.pharmacyId,
      name: medication.tagMetaData.rxDrugInfo.pharmacyName,
      externalId: medication.tagMetaData.rxDrugInfo.pharmacyCode,
      zipCode: medication.tagMetaData.rxDrugInfo.pharmacyZipCode,
    };
  };

  // Initialize update or refill mode data
  useEffect(() => {
    if (
      (mode === MEDICATION_MODE.UPDATE || mode === MEDICATION_MODE.REFILL) &&
      existingMedication &&
      isOpen
    ) {
      // For refill/update, we can work with medication data even if options aren't loaded yet
      // Transform will use medication.name as fallback if options not available
      const transformedMedication = transformExistingMedication(existingMedication, options);
      if (transformedMedication) {
        setSelectedItemsForm([transformedMedication]);
        setSelectedMedication([existingMedication.domainElementId]);

        // For refill/update mode, also set pharmacy
        const pharmacy = transformPharmacyFromMedication(existingMedication);
        if (pharmacy) {
          setSelectedPharmacy(pharmacy);
        }
      }
    }
  }, [mode, existingMedication, options, isOpen]);

  // Cleanup socket listeners on unmount
  useEffect(() => {
    return () => {
      // Only cleanup if not currently saving
      if (socket) {
        socket.off('mdtoolbox_automation_start');
        socket.off('mdtoolbox_automation_end');
        socket.off('automation_status_updates');
        socket.off('user_input_request');
        socket.off('user_input_response');
      }
    };
  }, [socket]);

  useEffect(() => {
    if (demographicDetails?.memberId) {
      getAssignedPrioritiesV2({
        patientId: demographicDetails?.memberId,
        dataDomainType: 'diagnoses',
        pageNumber: 0,
        pageSize: 100,
      })
        .then(({ data }) => {
          setPatientDiagnoses(data.records);
        })
        .catch(e => {
          console.error(e);
          setPatientDiagnoses([]);
        });
    }
  }, [demographicDetails]);

  const handleOutsideClick = () => {
    // Prevent closing during medication submission or when automation is running
    if (isSaving || isUIFrozen || isAutomationLoading) {
      return;
    }

    // For UPDATE/REFILL, always close (they started at review step, going back means closing)
    if (mode === MEDICATION_MODE.UPDATE || mode === MEDICATION_MODE.REFILL) {
      onClose();
      return;
    }

    // For ADD mode, step-by-step back logic
    if (currentStep === 0) {
      onClose();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const onCloseClick = () => {
    // Prevent closing during medication submission
    if (isSaving || isUIFrozen || isAutomationLoading) {
      return;
    }
    // Close drawer immediately for cross button or cancel button
    onClose();
  };

  const renderStep = step => {
    switch (step) {
      case 0:
        // Skip step 0 in update/refill mode
        if (mode === MEDICATION_MODE.UPDATE || mode === MEDICATION_MODE.REFILL) return null;
        return (
          <StepOne
            selectedItems={selectedMedication}
            setSelectedItems={setSelectedMedication}
            options={options}
            searchLabel="Select one medication"
            singleSelection={true}
            excludeLabels={['Other']}
          />
        );
      case 1:
        return (
          <StepTwoMedications
            selectedItems={selectedItemsForm}
            updateItems={setSelectedItemsForm}
            lookupData={lookupData}
            patientDiagnoses={patientDiagnoses}
            showInterference
            options={options}
            mode={mode}
          />
        );
      case 2:
        return (
          <StepFourMedications
            selectedMedications={selectedItemsForm}
            selectedPharmacy={selectedPharmacy}
            onPharmacySelect={setSelectedPharmacy}
            memberAddress={getMemberAddress()}
            onGoToMedicationStep={() => setCurrentStep(1)}
            isAutomationRunning={isAutomationRunning}
            isAutomationLoading={isAutomationLoading}
            automationMessages={automationMessages}
            automationStatus={automationStatus}
            userInputRequest={userInputRequest}
            onUserInputResponse={handleUserInputResponse}
            isUIFrozen={isUIFrozen}
            isSaving={isSaving}
            securityCodeAttempts={securityCodeAttempts}
            setSecurityCodeAttempts={setSecurityCodeAttempts}
            mode={mode}
            patientName={
              demographicDetails?.firstName && demographicDetails?.lastName
                ? `${demographicDetails.firstName} ${demographicDetails.lastName}`
                : demographicDetails?.fullName || ''
            }
            patientId={demographicDetails?.uuid || ''}
            zipCode={getPreferredPharmacyZip()}
            memberPreferredPharmacy={demographicDetails?.preferredPharmacy}
          />
        );
      default:
        return null;
    }
  };

  const checkDisabled = () => {
    // For refill/update mode at step 2, only check pharmacy
    if ((mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE) && currentStep === 2) {
      return !selectedPharmacy;
    }

    if (currentStep === 0 && mode === MEDICATION_MODE.ADD) {
      return selectedMedication.length === 0;
    }
    if (currentStep === 1) {
      // Check if medication form is complete
      const medication = selectedItemsForm[0];

      if (!medication) return true;

      // Check required fields directly from the medication object
      const requiredFields = ['quantity', 'dose', 'frequency', 'duration', 'dispenseAmount', 'sig'];

      return (
        !medication.externalMedicationId ||
        requiredFields.some(field => {
          const value = medication[field];
          if (field === 'quantity' || field === 'duration') {
            return !value || Number(value) <= 0;
          }
          if (field === 'frequency') {
            return !value || !value.value;
          }
          return !value;
        })
      );
    }
    if (currentStep === 2) {
      return !selectedPharmacy;
    }
    return false;
  };

  // Prevent space key from triggering navigation when drawer is open and user is typing
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = event => {
      // Check if space key is pressed and focus is on an input/textarea/autocomplete
      if (event.key === ' ' || event.keyCode === 32) {
        const activeElement = document.activeElement;
        const isInputField =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.getAttribute('role') === 'combobox' ||
          activeElement?.getAttribute('aria-autocomplete') !== null;

        if (isInputField) {
          event.stopPropagation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  const renderContent = () => (
    <Box className={classes.wrapper}>
      <Box className={clsx(classes.header, { [classes.headerNoDrawer]: isNoDrawer })}>
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Text weight={fontWeight.BOLD} className={classes.headTitle}>
              {mode === MEDICATION_MODE.REFILL
                ? 'Refill Medication'
                : mode === MEDICATION_MODE.UPDATE
                ? 'Update Medication'
                : 'New Medication'}
            </Text>
            <IconButton
              icon="close"
              className={classes.closeBtn}
              onClick={onCloseClick}
              disabled={isSaving}
            />
          </Box>
          {mode !== MEDICATION_MODE.REFILL && mode !== MEDICATION_MODE.UPDATE && (
            <Text weight={fontWeight.BOLD} className={classes.headSubtitle}>
              {stepTitlesMedications[currentStep]}
            </Text>
          )}
        </Box>
        {mode !== MEDICATION_MODE.REFILL && mode !== MEDICATION_MODE.UPDATE && (
          <Box className={classes.stepWrap}>
            <Stepper steps={['Select', 'Detail', 'Review']} activeStep={currentStep} />
          </Box>
        )}
      </Box>
      <Box
        className={clsx(classes.content, {
          [classes.contentNoDrawer]: isNoDrawer,
          [classes.contentStep2]: currentStep > 0,
          [classes.contentStep3]: currentStep === 2,
          [classes.contentRefill]: mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE,
        })}
      >
        {optionsLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" gap={4}>
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            Loading...
          </Box>
        ) : (
          renderStep(currentStep)
        )}
      </Box>
      <Box className={classes.footer}>
        <Button
          variant={btnType.TEXT}
          onClick={onCloseClick}
          disabled={isSaving || isUIFrozen || isAutomationLoading || isAutomationRunning}
        >
          Cancel
        </Button>

        {/* Hide submit button when medication is being saved or automation has started */}
        {!isAutomationLoading && !isAutomationRunning && !hasStartedAutomation && (
          <Button onClick={onClickContinue} disabled={checkDisabled() || isSaving}>
            {isSaving
              ? 'Saving...'
              : currentStep === 2
              ? continueButtonText ||
                (mode === MEDICATION_MODE.REFILL
                  ? 'Send Refill RX'
                  : mode === MEDICATION_MODE.UPDATE
                  ? 'Update medication'
                  : 'Send RX')
              : 'Continue'}
          </Button>
        )}
      </Box>
    </Box>
  );

  if (isNoDrawer) {
    return renderContent();
  }

  return (
    <Drawer
      open={isOpen}
      onClose={isSaving ? undefined : handleOutsideClick}
      variant={drawerType.NORMAL}
      className={classes.drawer}
      disableEscapeKeyDown={isSaving}
      disableBackdropClick={isSaving}
    >
      {renderContent()}
    </Drawer>
  );
};

export default AddNewReportMedications;
